// Modern Referral Widget Component with Binance-style design
import React, { useState, useEffect } from 'react';

/**
 * Modern Referral Widget for PGC Whitelist Form with Binance-style design
 */
export function ReferralWidget({ userId, userEmail }) {
  const [referralCode, setReferralCode] = useState('');
  const [referralUrl, setReferralUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({
    referrals: 0,
    rewards: 0,
    rank: 0
  });

  // Generate referral code on mount
  useEffect(() => {
    generateReferralCode();
    // Simulate loading stats
    loadStats();
  }, [userId]);

  const generateReferralCode = () => {
    // Simple referral code generation
    const timestamp = Date.now().toString(36);
    const userHash = userId ? userId.replace(/[^a-zA-Z0-9]/g, '').substring(0, 6) : 'user';
    const random = Math.random().toString(36).substr(2, 6);
    
    const code = `${userHash}${timestamp}${random}`.toUpperCase().substring(0, 12);
    const url = `${window.location.origin}/referral/${code}`;
    
    setReferralCode(code);
    setReferralUrl(url);
  };

  const loadStats = () => {
    // Simulate loading referral stats
    setTimeout(() => {
      setStats({
        referrals: Math.floor(Math.random() * 50),
        rewards: Math.floor(Math.random() * 5000),
        rank: Math.floor(Math.random() * 1000)
      });
    }, 500);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareReferral = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Join the $PGC Whitelist!',
        text: 'Get exclusive access to the $PGC token airdrop by joining the whitelist!',
        url: referralUrl
      }).catch(console.error);
    } else {
      copyToClipboard();
    }
  };

  if (!referralCode) {
    return (
      <div className="text-center py-4">
        <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6 mb-6 shadow-xl">
      {/* Header with trophy icon */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center mr-3">
            <span className="text-2xl">🏆</span>
          </div>
          <div>
            <h3 className="font-bold text-white text-lg">Refer Friends & Earn Rewards!</h3>
            <p className="text-gray-400 text-sm">Share your unique referral link</p>
          </div>
        </div>
        
        {/* Stats badges */}
        <div className="hidden sm:flex items-center space-x-2">
          <div className="bg-gray-700 rounded-full px-3 py-1 text-xs text-yellow-500">
            🏆 Rank #{stats.rank}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-gray-700 rounded-lg p-3 text-center">
          <div className="text-yellow-500 font-bold text-lg">{stats.referrals}</div>
          <div className="text-gray-400 text-xs">Referrals</div>
        </div>
        <div className="bg-gray-700 rounded-lg p-3 text-center">
          <div className="text-green-500 font-bold text-lg">{stats.rewards}</div>
          <div className="text-gray-400 text-xs">$PGC Earned</div>
        </div>
        <div className="bg-gray-700 rounded-lg p-3 text-center">
          <div className="text-blue-500 font-bold text-lg">+100</div>
          <div className="text-gray-400 text-xs">Per Referral</div>
        </div>
      </div>

      {/* Referral Link */}
      <div className="mb-4">
        <label className="block text-gray-400 text-sm font-medium mb-2">
          Your Referral Code
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <div className="flex rounded-lg overflow-hidden">
              <div className="bg-gray-700 px-3 py-3 text-sm font-mono text-yellow-500 truncate flex items-center">
                <span className="hidden sm:inline mr-2">🔗</span>
                {referralCode}
              </div>
              <button
                onClick={copyToClipboard}
                className={`px-4 py-3 font-medium transition-colors duration-200 flex items-center justify-center ${
                  copied 
                    ? 'bg-green-600 text-white' 
                    : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                }`}
              >
                {copied ? (
                  <>
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>
          
          <button
            onClick={shareReferral}
            className="px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
            </svg>
            Share
          </button>
        </div>
      </div>

      {/* Reward Info */}
      <div className="bg-gradient-to-r from-green-900 to-blue-900 bg-opacity-30 rounded-lg p-4 border border-green-500 border-opacity-30">
        <div className="flex items-start">
          <span className="text-2xl mr-3">🎁</span>
          <div>
            <h4 className="font-bold text-green-400 mb-1">Earn Bonus Tokens!</h4>
            <p className="text-green-300 text-sm">
              Get <strong className="text-yellow-400">100 $PGC</strong> tokens for each friend who successfully joins the whitelist.
            </p>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="mt-4 pt-4 border-t border-gray-700">
        <h4 className="font-semibold text-gray-300 mb-2">How it works:</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-gray-400">
          <div className="flex items-center">
            <span className="text-yellow-500 mr-2">1.</span>
            <span>Share your link</span>
          </div>
          <div className="flex items-center">
            <span className="text-yellow-500 mr-2">2.</span>
            <span>Friend joins</span>
          </div>
          <div className="flex items-center">
            <span className="text-yellow-500 mr-2">3.</span>
            <span>You earn tokens</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReferralWidget;